import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useRouter } from 'next/router'

import { initialValues } from '@constants/form/myWritingSearchForm/initialValues'
import { useAuthentication } from '@hooks/useAuthentication'
import { usePagination } from '@hooks/usePagination'
import { WritingListStatusEnum } from '@interfaces/WritingListStatusEnum'
import { MyWritingPageProps } from '@features/myWriting/pages/MyWritingPage/interface'
import { SearchFormType } from '@features/myWriting/pages/MyWritingPage/components/SearchForm/interface'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { scrollToElement, getNextTimeSelection } from '@lib/utils'
import { BookEnum } from '@interfaces/BookEnum'
import { useMyWritingAction } from '@hooks/book/myWriting/useMyWritingAction'

function ConfirmRemoveContent() {
  return (
    <div className='flex flex-col items-center text-secondary dark:text-white-2 mb-[20px] mx-[15px] desktop-tablet:w-[400px]'>
      <h1 className='text-[24px] font-medium font-mitr text-center'>
        ยืนยันการลบข้อมูลหรือไม่
      </h1>
      <div className='text-center text-sm font-light mt-[10px] max-w-[340px]'>
        หากคุณยืนยันจะลบงานเขียน ข้อมูลตอนและตัวละครทั้งหมดจะถูกลบ
      </div>
    </div>
  )
}

export function withMyWritingPage(Component: React.FC<MyWritingPageProps>) {
  function WithMyWritingPage() {
    const router = useRouter()
    const myWritingClient = useMyWritingAction()
    const advanceSearch = router.query.advanceSearch === 'true'
    const { isAuthenticated, isUserLoading } = useAuthentication()
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const [searchParam, setSearchParam] =
      useState<SearchFormType>(initialValues)
    const confirmModal = useModal({ modal: 'confirm' }, true)
    const alertModal = useModal({ modal: 'alert' }, true)
    const loadingModal = useModal({ modal: 'loading' }, true)
    const storySettingPublishEndTime = useModal(
      {
        modal: 'storySettingPublishEndTime',
      },
      true
    )
    const alert = useAlert()
    const tab = router.query.tab || 'myWriting'

    const {
      data: myWriting,
      isLoading,
      refetch,
    } = useQuery(
      [
        'my-writing',
        'page',
        page,
        'limitPerPage',
        perpage,
        'searchParam',
        searchParam,
      ],
      () => myWritingClient.getMyWriting(page, perpage, searchParam),
      {
        enabled: isAuthenticated && tab === 'myWriting',
      }
    )

    async function handleSubmit(val: SearchFormType) {
      pageChange(1)

      const query: any = { tab: 'myWriting', advanceSearch }
      if (val.param.length) query.text = val.param.trim()
      if (val.bookType.length) query.bookType = val.bookType
      if (val.category.length) query.category = val.category
      if (val.penName.length) query.penName = val.penName

      router.replace({
        pathname: '/my-writing',
        query,
      })
    }

    async function changeStatusGroup(ids: number[], status: string) {
      if (status === WritingListStatusEnum.DELETE) {
        const hasAmountSpent = myWriting?.data.some(
          row =>
            ids.includes(row.id) &&
            (row.amountSpent > 0 || row.totalEbookSeries > 0)
        )

        if (hasAmountSpent) {
          alertModal.show({
            content: (
              <div className='text-center'>
                <div className='font-bold text-[24px]'>
                  {ids.length === 1
                    ? 'ไม่สามารถลบเรื่องนี้ได้'
                    : 'ไม่สามารถลบเรื่องที่เลือกได้'}
                </div>
                {ids.length === 1
                  ? 'เนื่องจากมีการขายไปแล้ว หรือมีการสร้างอีบุ๊กจากเรื่องนี้'
                  : 'เนื่องจากบางเรื่องมีการขายไปแล้ว หรือมีการสร้างอีบุ๊กจากเรื่องนี้'}
              </div>
            ),
            onClose: () => alertModal.hide(),
          })

          return
        }

        confirmModal.show({
          content: <ConfirmRemoveContent />,
          onConfirm: async () => {
            try {
              await myWritingClient.removeBooks(ids)
              alert.success('ลบงานเขียนสำเร็จ')
              refetch()
            } catch (_) {
              alert.error('เกิดข้อผิดพลาด ลบงานเขียนไม่สำเร็จ')
            } finally {
              confirmModal.hide()
            }
          },
          onClose: () => confirmModal.hide(),
        })
      } else {
        try {
          await myWritingClient.updateBookStatus(ids, status)
          alert.success('แก้ไขสถานะงานเขียนสำเร็จ')
          refetch()
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      }
    }

    async function changeStatus(id: number, status: string) {
      if (status === WritingListStatusEnum.END_SCHEDULE) {
        storySettingPublishEndTime.show({
          bookId: id,
          initialValues: { datetime: getNextTimeSelection() },
          refetch,
        })
        return
      }

      if (status === WritingListStatusEnum.CANCEL_END_SCHEDULE) {
        confirmModal.show({
          content: (
            <div className='text-center text-[24px] text-secondary dark:text-white-2 font-mitr font-medium mb-[20px] desktop-tablet:w-[400px]'>
              ยืนยันยกเลิกการตั้งเวลาหรือไม่?
            </div>
          ),
          onConfirm: async () => {
            loadingModal.show()
            myWritingClient
              .updateUnpublishBook(id, null)
              .then(() => {
                alert.success('ยกเลิกการตั้งเวลาปิดเผยแพร่เรียบร้อยแล้ว')
                refetch()
              })
              .catch(() => {
                alert.error('เกิดข้อผิดพลาด ในการยกเลิกการตั้งเวลาปิดเผยแพร่')
              })
              .finally(() => {
                loadingModal.hide()
                confirmModal.hide()
              })
          },
          onClose: () => {
            confirmModal.hide()
          },
        })
        return
      }

      if (status === WritingListStatusEnum.DELETE) {
        const book = myWriting?.data.find(
          row =>
            row.id === id && (row.amountSpent > 0 || row.totalEbookSeries > 0)
        )

        if (book) {
          alertModal.show({
            content: (
              <div className='text-center'>
                <div className='font-bold text-[24px]'>
                  ไม่สามารถลบเรื่องนี้ได้
                </div>
                เนื่องจากมีการขายไปแล้ว หรือมีการสร้างอีบุ๊กจากเรื่องนี้
              </div>
            ),
            onClose: () => alertModal.hide(),
          })

          return
        }

        confirmModal.show({
          content: <ConfirmRemoveContent />,
          onConfirm: async () => {
            try {
              await myWritingClient.removeBook(id)
              alert.success('ลบงานเขียนสำเร็จ')
              refetch()
            } catch (_) {
              alert.error('เกิดข้อผิดพลาด ลบงานเขียนไม่สำเร็จ')
            } finally {
              confirmModal.hide()
            }
          },
          onClose: () => confirmModal.hide(),
        })
      } else {
        try {
          await myWritingClient.updateBookStatus(id, status)
          alert.success('แก้ไขสถานะงานเขียนสำเร็จ')
          refetch()
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#my-writing')
    }

    function handleTabChange(value: string) {
      router.replace({
        pathname: '/my-writing',
        query: {
          tab: value,
        },
      })
    }

    useEffect(() => {
      const param = (router.query.text as string) || ''
      const bookType = router.query.bookType
        ? typeof router.query.bookType === 'string'
          ? ([router.query.bookType] as BookEnum[])
          : (router.query.bookType as BookEnum[])
        : []
      const category = router.query.category
        ? typeof router.query.category === 'string'
          ? [router.query.category]
          : router.query.category
        : []
      const penName = router.query.penName
        ? typeof router.query.penName === 'string'
          ? [router.query.penName]
          : router.query.penName
        : []

      setSearchParam({
        param,
        bookType,
        category,
        penName,
      })
    }, [
      router.query.text,
      router.query.bookType,
      router.query.category,
      router.query.penName,
    ])

    const pageProps = {
      initialValues: searchParam,
      page,
      perpage,
      total: myWriting?.total || 0,
      myWritingList: myWriting?.data || [],
      isLoading: isUserLoading || isLoading,
      handlePaginationChange,
      perpageChange,
      handleSubmit,
      changeStatusGroup,
      changeStatus,
      handleTabChange,
    }
    return <Component {...pageProps} />
  }

  return WithMyWritingPage
}
